import Phaser from "phaser";
import { artTextureKey, markArtLoaded, type SpriteManifest } from "@/sprites/artManifest";

/**
 * Per-scene asset packs (v0.11 RFC §3.2). PreloadScene loads core+office
 * and registers itself here as the HOST — the one always-running scene
 * whose Loader carries every later pack (a slept scene's loader stalls,
 * so tier scenes can't load their own art; the host never sleeps).
 *
 * Contract mirrors the v0.7 art layer: packs are best-effort. A file that
 * fails inside a pack leaves its placeholder (loud console via the host's
 * FILE_LOAD_ERROR handler); ensurePack always RESOLVES (never rejects) —
 * the veil must clear and the scene must open with whatever landed.
 */

const CONTINUUM_ORDER = ["detox", "resi", "php", "iop", "sober", "alumni"] as const;

export { packForScene } from "@/sprites/artManifest";

let host: Phaser.Scene | null = null;
let manifest: SpriteManifest | null = null;
const loadedPacks = new Set<string>();
const inflight = new Map<string, Promise<void>>();

/**
 * Called once by PreloadScene.create(): the packs it already loaded are
 * marked done; everything else becomes lazily loadable. A v1 manifest
 * (or none) means all art was handled up front — every pack reports
 * ready and ensurePack is a no-op.
 */
export function registerArtHost(scene: Phaser.Scene, m: SpriteManifest | null, preloadedPacks: string[]): void {
  host = scene;
  manifest = m;
  for (const p of preloadedPacks) loadedPacks.add(p);
}

export function packReady(pack: string): boolean {
  if (manifest === null || manifest.version === 1) return true;
  return loadedPacks.has(pack);
}

/** Sprite entries belonging to a pack (v2 manifests only). */
function packEntries(pack: string): Array<[string, SpriteManifest["sprites"][string]]> {
  if (manifest === null) return [];
  return Object.entries(manifest.sprites).filter(([, e]) => e.pack === pack);
}

function assetUrl(fileRelToPublic: string): string {
  return `${import.meta.env.BASE_URL}${fileRelToPublic}`;
}

/**
 * Load a pack on the host's Loader; idempotent and share-in-flight. Always
 * resolves (load errors degrade to placeholders; a wedged loader is cut
 * loose by the safety timeout so a veil can never hang the game).
 */
export function ensurePack(pack: string): Promise<void> {
  if (packReady(pack)) return Promise.resolve();
  const pending = inflight.get(pack);
  if (pending) return pending;
  const h = host;
  if (h === null) return Promise.resolve(); // pre-boot call: preload path owns it
  const entries = packEntries(pack).filter(([key]) => !h.textures.exists(artTextureKey(key)));
  const run = new Promise<void>((resolve) => {
    const finish = (): void => {
      for (const [key] of packEntries(pack)) {
        if (h.textures.exists(artTextureKey(key))) markArtLoaded(key);
      }
      loadedPacks.add(pack);
      inflight.delete(pack);
      resolve();
    };
    if (entries.length === 0) {
      finish();
      return;
    }
    for (const [key, entry] of entries) {
      if (entry.frames !== undefined && entry.frames > 1) {
        h.load.spritesheet(artTextureKey(key), assetUrl(entry.file), {
          frameWidth: entry.frameWidth,
          frameHeight: entry.frameHeight,
          endFrame: entry.frames - 1,
        });
      } else {
        h.load.image(artTextureKey(key), assetUrl(entry.file));
      }
    }
    const timeout = window.setTimeout(() => {
      console.warn(`[art] pack "${pack}" load timed out — opening with placeholders`);
      finish();
    }, 20000);
    h.load.once(Phaser.Loader.Events.COMPLETE, () => {
      window.clearTimeout(timeout);
      finish();
    });
    h.load.start();
  });
  inflight.set(pack, run);
  return run;
}

/**
 * Idle-warm every remaining pack in continuum order (RULED §8.4:
 * everything), one at a time so a real navigation's ensurePack shares or
 * overtakes the queue instead of waiting behind six packs.
 */
export function prefetchAllPacks(): void {
  const queue = CONTINUUM_ORDER.filter((p) => !packReady(p));
  const next = (): void => {
    // Yield the pipe: if ANY load is live (e.g. a tapped tier behind the
    // veil), hold the warm queue — the old comment claimed navigations
    // "overtake"; on one shared Loader they'd actually share bandwidth
    // (v0.11 review, perf lens). Deferring is the honest priority.
    if (inflight.size > 0) {
      idle(next);
      return;
    }
    const pack = queue.shift();
    if (pack === undefined) return;
    void ensurePack(pack).then(() => idle(next));
  };
  const idle = (fn: () => void): void => {
    // typeof-check (not `in`) — Safari <18 lacks requestIdleCallback.
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(() => fn(), { timeout: 4000 });
    else window.setTimeout(fn, 500);
  };
  idle(next);
}

/** Test-only: reset module state between cases. */
export function _resetArtPacks(): void {
  host = null;
  manifest = null;
  loadedPacks.clear();
  inflight.clear();
}
